// Channel-first registration flow — static config.
// Channel IDs map to backend PlatformApplication / connection enums; align naming
// with the backend before wiring the registration API call (see ticket #39124).

import logoShopify from "../../_assets/img/registration/logo-shopify.svg";
import logoAmazon from "../../_assets/img/registration/logo-amazon.png";
import logoWooCommerce from "../../_assets/img/registration/logo-woocommerce.svg";
import logoEbay from "../../_assets/img/registration/logo-ebay.svg";
import logoTikTok from "../../_assets/img/registration/logo-tiktok.svg";
import logoKaufland from "../../_assets/img/registration/logo-kaufland.png";
import logoOtto from "../../_assets/img/registration/logo-otto.png";
import logoZalando from "../../_assets/img/registration/logo-zalando.png";
import logoAboutYou from "../../_assets/img/registration/logo-aboutyou.png";
import logoStripe from "../../_assets/img/registration/logo-stripe.svg";
import logoChargebee from "../../_assets/img/registration/logo-chargebee.png";

export const CHANNEL_GROUPS = [
    {
        label: "eCommerce",
        // Order matches Register v4 (website layout): TikTok Shop at position 3 so the
        // higher-converting channel surfaces above the fold on mobile (#42099).
        // WooCommerce sits last and uses a compact label — it's the longest name and
        // would otherwise clip/wrap in a narrow mobile card.
        channels: [
            { id: "SHOPIFY", label: "Shopify", logo: logoShopify },
            { id: "AMAZON", label: "Amazon", logo: logoAmazon },
            { id: "TIKTOK_SHOP", label: "TikTok Shop", logo: logoTikTok },
            { id: "EBAY", label: "eBay", logo: logoEbay },
            { id: "KAUFLAND", label: "Kaufland", logo: logoKaufland },
            { id: "OTTO", label: "Otto", logo: logoOtto },
            { id: "ZALANDO", label: "Zalando", logo: logoZalando },
            { id: "ABOUT_YOU", label: "About You", logo: logoAboutYou },
            { id: "WOO_COMMERCE", label: "WooCommerce", logo: logoWooCommerce, compactLabel: true },
        ],
    },
    {
        label: "SaaS-Abrechnung",
        channels: [
            { id: "STRIPE_BILLING", label: "Stripe Billing", logo: logoStripe },
            { id: "CHARGEBEE", label: "Chargebee", logo: logoChargebee },
        ],
    },
];

// Referrer / ?channel= pre-selection. CONTAINS-based matching (not exact slug),
// config-driven so a new marketplace LP only needs an entry here — no code change.
// keyword (lowercased) -> channel ID
export const REFERRER_CHANNEL_KEYWORDS = {
    amazon: "AMAZON",
    woocommerce: "WOO_COMMERCE",
    shopify: "SHOPIFY",
    ebay: "EBAY",
    tiktok: "TIKTOK_SHOP",
    kaufland: "KAUFLAND",
    otto: "OTTO",
    zalando: "ZALANDO",
    aboutyou: "ABOUT_YOU",
    "about-you": "ABOUT_YOU",
    stripe: "STRIPE_BILLING",
    "stripe-billing": "STRIPE_BILLING",
    chargebee: "CHARGEBEE",
};

export const REGISTRATION_STEPS = {
    CHANNELS: 1,
    COMPANY: 2,
    EMAIL: 3,
    SUCCESS: 4,
};

// Progress bar: 33% -> 66% -> 100% across the three input steps.
export const STEP_PROGRESS = {
    [REGISTRATION_STEPS.CHANNELS]: 33,
    [REGISTRATION_STEPS.COMPANY]: 66,
    [REGISTRATION_STEPS.EMAIL]: 100,
    [REGISTRATION_STEPS.SUCCESS]: 100,
};

// Derive pre-selected channels from the entry URL. Prefers an explicit ?channel=
// param, falls back to document.referrer contains-matching.
export const resolvePreselectedChannels = (search, referrer) => {
    const matched = new Set();

    const params = new URLSearchParams(search || "");
    const explicit = params.get("channel");
    if (explicit) {
        const key = explicit.toLowerCase();
        // Own-property check so keys like "__proto__"/"constructor" can't resolve to inherited members.
        const id = Object.prototype.hasOwnProperty.call(REFERRER_CHANNEL_KEYWORDS, key)
            ? REFERRER_CHANNEL_KEYWORDS[key]
            : undefined;
        if (id) matched.add(id);
    }

    const haystack = `${search || ""} ${referrer || ""}`.toLowerCase();
    Object.entries(REFERRER_CHANNEL_KEYWORDS).forEach(([keyword, id]) => {
        if (haystack.includes(keyword)) matched.add(id);
    });

    return Array.from(matched);
};
